import React from "react";
import { useTranslation } from "react-i18next";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import type {
  BookingAfterUnit,
  Channel,
  SenderType,
  StaffOption,
} from "../utils/types";
import { ArrivalChannelTabs } from "./ArrivalChannelTabs";
import { MessagingSettingsCardFooter } from "./MessagingSettingsCardFooter";
import { SenderTypePicker } from "./SenderTypePicker";
import { StaffAssignmentPanel } from "./StaffAssignmentPanel";

type BookingConfirmationCardProps = {
  bookingAutoSend: boolean;
  onBookingAutoSendChange: (value: boolean) => void;
  bookingAfterValue: number;
  onBookingAfterValueChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  bookingAfterUnit: BookingAfterUnit;
  onBookingAfterUnitChange: (unit: BookingAfterUnit) => void;
  bookingReminderDays: number;
  onReminderDaysChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  channel: Channel;
  onChannelChange: (channel: Channel) => void;
  senderType: SenderType;
  onSenderTypeChange: (value: SenderType) => void;
  staffId: string;
  onStaffIdChange: (id: string) => void;
  fallback: string;
  onFallbackChange: (value: string) => void;
  staffOptions: StaffOption[];
  selectedStaff?: StaffOption;
  onCancel: () => void;
  onSave: () => void;
  isSaving?: boolean;
};

export function BookingConfirmationCard({
  bookingAutoSend,
  onBookingAutoSendChange,
  bookingAfterValue,
  onBookingAfterValueChange,
  bookingAfterUnit,
  onBookingAfterUnitChange,
  bookingReminderDays,
  onReminderDaysChange,
  channel,
  onChannelChange,
  senderType,
  onSenderTypeChange,
  staffId,
  onStaffIdChange,
  fallback,
  onFallbackChange,
  staffOptions,
  selectedStaff,
  onCancel,
  onSave,
  isSaving,
}: BookingConfirmationCardProps) {
  const { t } = useTranslation();

  return (
    <Card className="max-w-4xl gap-0 py-0 shadow-sm">
      <CardHeader className="mb-6 border-0 px-6 pt-6 pb-0">
        <CardTitle className="text-foreground text-2xl font-semibold tracking-tight">
          {t("layout.messagingSettingsPage.tabs.bookingConfirmation")}
        </CardTitle>
        <CardDescription>
          {t("layout.messagingSettingsPage.bookingConfirmationDescription")}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6 px-6 pt-0 pb-2">
        <div className="border-border bg-muted/20 flex flex-col gap-4 rounded-xl border p-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="space-y-1 pr-4">
            <Label
              htmlFor="booking-auto-send"
              className="text-foreground text-base font-semibold"
            >
              {t("layout.messagingSettingsPage.bookingAutoSendLabel")}
            </Label>
            <p
              id="booking-auto-send-description"
              className="text-muted-foreground text-sm"
            >
              {t("layout.messagingSettingsPage.bookingAutoSendDescription")}
            </p>
          </div>
          <Switch
            id="booking-auto-send"
            checked={bookingAutoSend}
            onCheckedChange={onBookingAutoSendChange}
            className="shrink-0"
            aria-describedby="booking-auto-send-description"
          />
        </div>

        <fieldset
          disabled={!bookingAutoSend}
          aria-label={t("layout.messagingSettingsPage.bookingTimingAriaLabel")}
          className={cn(
            "m-0 min-w-0 space-y-6 border-0 p-0",
            !bookingAutoSend && "opacity-50",
          )}
        >
          <div className="space-y-3">
            <div>
              <Label
                id="booking-interval-after-title"
                className="text-foreground text-base font-semibold"
              >
                {t("layout.messagingSettingsPage.bookingIntervalAfterTitle")}
              </Label>
              <p
                id="booking-interval-after-hint"
                className="text-muted-foreground mt-1 text-sm"
              >
                {t("layout.messagingSettingsPage.bookingIntervalAfterHint")}
              </p>
            </div>
            <div className="flex max-w-md flex-wrap items-end gap-3">
              <div className="w-24 shrink-0 space-y-2">
                <Label htmlFor="booking-after-value" className="sr-only">
                  {t("layout.messagingSettingsPage.bookingIntervalAfterTitle")}
                </Label>
                <Input
                  id="booking-after-value"
                  type="number"
                  min={1}
                  aria-describedby="booking-interval-after-hint"
                  value={bookingAfterValue}
                  onChange={onBookingAfterValueChange}
                  className="border-base-border h-12 w-full bg-white"
                />
              </div>
              <Select
                value={bookingAfterUnit}
                onValueChange={(v) =>
                  onBookingAfterUnitChange(v as BookingAfterUnit)
                }
              >
                <SelectTrigger
                  id="booking-after-unit"
                  size="md"
                  className="border-base-border h-12 min-w-[140px] bg-white"
                  aria-labelledby="booking-interval-after-title"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="minutes">
                    {t("layout.messagingSettingsPage.bookingMinutes")}
                  </SelectItem>
                  <SelectItem value="hours">
                    {t("layout.messagingSettingsPage.bookingHours")}
                  </SelectItem>
                </SelectContent>
              </Select>
              <p className="text-muted-foreground pb-3 text-sm">
                {t("layout.messagingSettingsPage.bookingIntervalAfterSuffix")}
              </p>
            </div>
          </div>

          <div className="space-y-3">
            <div>
              <Label
                id="booking-interval-reminder-title"
                className="text-foreground text-base font-semibold"
              >
                {t("layout.messagingSettingsPage.bookingIntervalReminderTitle")}
              </Label>
              <p
                id="booking-interval-reminder-hint"
                className="text-muted-foreground mt-1 text-sm"
              >
                {t("layout.messagingSettingsPage.bookingIntervalReminderHint")}
              </p>
            </div>
            <div className="flex max-w-md flex-wrap items-end gap-3">
              <div className="w-24 shrink-0 space-y-2">
                <Label htmlFor="booking-reminder-days" className="sr-only">
                  {t(
                    "layout.messagingSettingsPage.bookingIntervalReminderTitle",
                  )}
                </Label>
                <Input
                  id="booking-reminder-days"
                  type="number"
                  min={1}
                  aria-describedby="booking-interval-reminder-hint"
                  value={bookingReminderDays}
                  onChange={onReminderDaysChange}
                  className="border-base-border h-12 w-full bg-white"
                />
              </div>
              <p className="text-muted-foreground pb-3 text-sm">
                {t(
                  "layout.messagingSettingsPage.bookingIntervalReminderSuffix",
                )}
              </p>
            </div>
          </div>
        </fieldset>

        <div className="space-y-4">
          <ArrivalChannelTabs
            channel={channel}
            onChannelChange={onChannelChange}
          />
          <SenderTypePicker
            senderType={senderType}
            onSenderTypeChange={onSenderTypeChange}
          />
          {senderType === "staff" && (
            <StaffAssignmentPanel
              staffId={staffId}
              onStaffIdChange={onStaffIdChange}
              fallback={fallback}
              onFallbackChange={onFallbackChange}
              staffOptions={staffOptions}
              selectedStaff={selectedStaff}
            />
          )}
        </div>
      </CardContent>
      <MessagingSettingsCardFooter
        onCancel={onCancel}
        onSave={onSave}
        isSaving={isSaving}
      />
    </Card>
  );
}
